/**
 * React Query Configuration
 * Standardized cache and retry settings for all queries
 */

/**
 * Cache configuration presets based on data change frequency
 */
export const CACHE_CONFIG = {
  /**
   * Fast-changing data (user activity, cart, search results)
   * - Stale after 30 seconds
   * - Garbage collected after 5 minutes
   */
  FAST_CHANGING: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  /**
   * Medium-changing data (orders, products, pricing)
   * - Stale after 5 minutes
   * - Garbage collected after 10 minutes
   */
  MEDIUM_CHANGING: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },

  /**
   * Slow-changing data (categories, settings, statuses)
   * - Stale after 10 minutes
   * - Garbage collected after 30 minutes
   */
  SLOW_CHANGING: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },

  /**
   * Static data (roles, constants, configurations)
   * - Stale after 1 hour
   * - Garbage collected after 24 hours
   */
  STATIC: {
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
  },
} as const;

/**
 * Retry configuration with exponential backoff
 * 
 * @param attemptIndex - Current retry attempt (0-indexed)
 * @returns Delay in milliseconds before next retry
 * 
 * @example
 * Attempt 0: 1000ms (1s)
 * Attempt 1: 2000ms (2s)
 * Attempt 2: 4000ms (4s)
 * Attempt 3+: 30000ms (30s) - capped
 */
export const retryDelay = (attemptIndex: number): number => {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
};

/**
 * Default query configuration
 * Apply to all queries unless overridden
 */
export const DEFAULT_QUERY_CONFIG = {
  ...CACHE_CONFIG.MEDIUM_CHANGING,
  retry: 3, // Retry failed requests 3 times
  retryDelay,
  refetchOnWindowFocus: false, // Don't refetch when window regains focus
  refetchOnReconnect: true, // Refetch when internet connection is restored
} as const;

/**
 * Configuration for search queries
 * Search results change frequently but we want to show cached results quickly
 */
export const SEARCH_QUERY_CONFIG = {
  ...CACHE_CONFIG.MEDIUM_CHANGING,
  retry: 2, // Fewer retries for search
  retryDelay,
  refetchOnWindowFocus: false,
} as const;

/**
 * Configuration for authentication queries
 * Auth state changes infrequently but we want to catch changes quickly
 */
export const AUTH_QUERY_CONFIG = {
  ...CACHE_CONFIG.MEDIUM_CHANGING,
  retry: 1, // Minimal retries for auth (fail fast)
  retryDelay,
  refetchOnWindowFocus: true, // Check auth status when user returns
} as const;

/**
 * Configuration for admin/management queries
 * Admin data changes moderately and we want consistency
 */
export const ADMIN_QUERY_CONFIG = {
  ...CACHE_CONFIG.SLOW_CHANGING,
  retry: 3,
  retryDelay,
  refetchOnWindowFocus: false,
} as const;

/**
 * Configuration for static/reference data
 * Rarely changes, can be cached aggressively
 */
export const STATIC_QUERY_CONFIG = {
  ...CACHE_CONFIG.STATIC,
  retry: 5, // More retries for static data
  retryDelay,
  refetchOnWindowFocus: false,
  refetchOnMount: false, // Don't refetch on component remount
} as const;

